/**
 * Customer address management API service
 */
import apiClient from './api';
import {
  CustomerAddress,
  CustomerAddressCreateData,
  CustomerAddressUpdateData,
  CustomerAddressListResponse,
  AddressHistoryResponse
} from '../types/address';

export class AddressService {
  // Get all addresses for a customer
  static async getCustomerAddresses(customerId: string, params?: {
    page?: number;
    per_page?: number;
    address_type?: string;
    is_active?: boolean;
  }): Promise<CustomerAddressListResponse> {
    const response = await apiClient.get(`/admin/customers/${customerId}/addresses`, { params });
    return response.data;
  }

  // Get a specific address
  static async getAddress(customerId: string, addressId: string): Promise<CustomerAddress> {
    const response = await apiClient.get(`/admin/customers/${customerId}/addresses/${addressId}`);
    return response.data;
  }

  // Create a new address
  static async createAddress(data: CustomerAddressCreateData): Promise<CustomerAddress> {
    const response = await apiClient.post(`/admin/customers/${data.customer_id}/addresses`, data);
    return response.data;
  }

  // Update an existing address
  static async updateAddress(
    customerId: string, 
    addressId: string, 
    data: CustomerAddressUpdateData
  ): Promise<CustomerAddress> {
    const response = await apiClient.put(`/admin/customers/${customerId}/addresses/${addressId}`, data);
    return response.data;
  }

  // Delete an address (soft delete - deactivates it)
  static async deleteAddress(customerId: string, addressId: string): Promise<CustomerAddress> {
    const response = await apiClient.delete(`/admin/customers/${customerId}/addresses/${addressId}`);
    return response.data;
  }

  // Set an address as default
  static async setDefaultAddress(customerId: string, addressId: string): Promise<CustomerAddress> {
    const response = await apiClient.post(`/admin/customers/${customerId}/addresses/${addressId}/set-default`);
    return response.data;
  }

  // Get address history
  static async getAddressHistory(customerId: string, addressId?: string, params?: {
    page?: number;
    per_page?: number;
  }): Promise<AddressHistoryResponse> {
    const endpoint = addressId 
      ? `/admin/customers/${customerId}/addresses/${addressId}/history`
      : `/admin/customers/${customerId}/addresses/history`;
    const response = await apiClient.get(endpoint, { params });
    return response.data;
  }
}